import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './shadcn/card';
import { Button } from './shadcn/button';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function ProtectedRoute({ children }) {
  const { currentUser, deletionStatus, deletionStatusChecked } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showLoader, setShowLoader] = useState(false);

  // Show loader for a brief moment when deletion status is being checked for the first time
  useEffect(() => {
    if (!deletionStatusChecked) {
      const timer = setTimeout(() => {
        setShowLoader(true);
      }, 500); // Show loader after 500ms if still checking
      
      return () => clearTimeout(timer);
    } else {
      setShowLoader(false);
    }
  }, [deletionStatusChecked]);

  // If there's no current user, redirect to login
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // If deletion status hasn't been checked yet, show loader briefly
  if (!deletionStatusChecked && showLoader) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If we've checked deletion status and found a pending deletion request, show overlay
  if (deletionStatusChecked && deletionStatus && deletionStatus.hasRequest && deletionStatus.status === 'pending') {
    // Allow access to compliance dashboard and account pages
    const allowedPaths = ['/compliance-dashboard'];
    if (!allowedPaths.includes(location.pathname)) {
      // Show the children (dashboard content) behind a faded overlay
      return (
        <div className="relative">
          {/* Dashboard content behind the overlay */}
          <div className="opacity-30 pointer-events-none">
            {children}
          </div>
          
          {/* Overlay with deletion message - faded background scrolls, message stays centered */}
          <div className="absolute inset-0 z-40">
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
            <div className="fixed inset-0 flex items-center justify-center pointer-events-none">
              <Card className="max-w-2xl w-full bg-white relative z-10 pointer-events-auto mx-4">
                <CardHeader>
                  <CardTitle className="flex items-center text-red-600">
                    <AlertTriangle className="mr-2 h-5 w-5" />
                    Account Pending Deletion
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-600">
                    Your account is currently scheduled for deletion. 
                    Access to this page is disabled during this period.
                  </p>
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">Reason:</span> {deletionStatus.reason}
                  </p>
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">Request Date:</span> {new Date(deletionStatus.requestDate).toLocaleDateString()}
                  </p>
                  <div className="flex gap-2 pt-4">
                    <Button 
                      onClick={() => navigate('/compliance-dashboard')}
                      className="flex-1"
                    >
                      View Deletion Request
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      );
    }
  }

  // If user is authenticated, render the children
  return children;
}

export default ProtectedRoute;